/**
 * Attachments are part of the question. If a file fails to upload, the answer
 * the assistant gives is about a document it never saw — so a failed upload
 * must block the send rather than being quietly skipped.
 */
export interface PendingUpload {
  fileName: string;
  uploadedUrl?: string;
  error?: string;
}

export interface SendPlan {
  /** Files still needing an upload, in send order. */
  toUpload: PendingUpload[];
  /** Files already in object storage from an earlier attempt. */
  alreadyUploaded: PendingUpload[];
  /** Files that failed; a non-empty list means the send must not proceed. */
  failed: PendingUpload[];
}

export function planSend(files: PendingUpload[]): SendPlan {
  return {
    toUpload: files.filter((f) => !f.uploadedUrl && !f.error),
    alreadyUploaded: files.filter((f) => Boolean(f.uploadedUrl)),
    failed: files.filter((f) => !f.uploadedUrl && Boolean(f.error)),
  };
}

export function canSendWith(files: PendingUpload[]): boolean {
  return files.length === 0 || planSend(files).failed.length === 0;
}

/** e.g. `2 uploads failed (report.pdf, data.csv)` */
export function describeFailedUploads(files: PendingUpload[]): string {
  const count = files.length;
  const names = files.map((f) => f.fileName);
  const shown = names.slice(0, 3).join(", ");
  const extra = names.length > 3 ? `, +${names.length - 3} more` : "";
  return `${count} upload${count === 1 ? "" : "s"} failed (${shown}${extra})`;
}
